import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * Emergent Google OAuth callback.
 *
 * The user lands on `/auth/callback?invite=<token>#session_id=<sid>`
 * after Google authentication. We exchange the session_id with our
 * backend (which sets the httpOnly `emergent_session` cookie), then
 * redirect to the invite page so the normal post-login UX runs.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash || "";
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    const query = new URLSearchParams(location.search || window.location.search || "");
    const inviteToken = query.get("invite");

    if (!sessionId) {
      setError("Sessão do Google não encontrada. Tente novamente.");
      return;
    }

    (async () => {
      try {
        await api.post(
          "/api/auth/google/callback",
          { session_id: sessionId, invite_token: inviteToken || undefined },
          { withCredentials: true }
        );
        // Clean the fragment from the URL and bounce to the invite page
        // (if we came from an invite) or the Journey home. A full reload
        // triggers AuthContext to re-detect the Emergent cookie session.
        const next = inviteToken ? `/convite/${encodeURIComponent(inviteToken)}` : "/";
        window.history.replaceState({}, "", next);
        window.location.replace(next);
      } catch (err) {
        setError(err?.response?.data?.detail || "Não conseguimos finalizar seu login com Google.");
      }
    })();
  }, [location.hash, location.search]);

  if (error) {
    return (
      <div
        data-testid="auth-callback-error"
        className="flex items-center justify-center min-h-screen bg-[#0B0C10] p-6"
      >
        <div className="max-w-sm text-center space-y-3 bg-[#14161C] border border-red-500/30 rounded-2xl p-6">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto" />
          <h1 className="text-base font-extrabold text-white">Login com Google falhou</h1>
          <p className="text-xs text-gray-400">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/", { replace: true })}
            className="px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="auth-callback-loading"
      className="flex items-center justify-center min-h-screen bg-[#0B0C10]"
    >
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-7 h-7 text-[#FF6B00] animate-spin" />
        <span className="text-xs font-semibold text-gray-400">Finalizando login com Google...</span>
      </div>
    </div>
  );
}
